// Heatmap capture helpers (ANALYTICS-SYSTEM-DESIGN.md §4.3).
//
// Three collectors, all aggregate-only signals:
//   1. observeInteractions   — element heatmap: clicks on [data-track-key]
//   2. observeCanvasClicks   — position heatmap: normalized (x, y) in the viewport
//   3. listenForViewerMessages — camera-attention heatmap + viewer-emitted events
//                                over the postMessage bridge

import { CANONICAL_EVENT_TYPES } from "./types";
import type { TrackFn } from "./types";

const CANONICAL_SET: ReadonlySet<string> = new Set<string>(CANONICAL_EVENT_TYPES);

/**
 * Delegated element-heatmap observer. One listener on the root; every click
 * (or pointerdown on elements that swallow clicks) resolves the nearest
 * `[data-track-key]` ancestor and emits `ui.interaction { key }`.
 *
 * Works inside Shadow DOM: attach it to each shadow root (the embed loader
 * does) or to document for the host SPA.
 */
export function observeInteractions(
  root: ShadowRoot | HTMLElement | Document,
  track: TrackFn,
): () => void {
  const handler = (e: Event) => {
    try {
      const target = e.target as Element | null;
      if (!target || typeof target.closest !== "function") return;
      const keyed = target.closest("[data-track-key]");
      if (!keyed) return;
      const key = keyed.getAttribute("data-track-key");
      if (!key) return;
      track("ui.interaction", { key });
    } catch {
      /* observers must never break the UI */
    }
  };
  try {
    root.addEventListener("click", handler);
  } catch {
    return () => undefined;
  }
  return () => {
    try {
      root.removeEventListener("click", handler);
    } catch {
      /* noop */
    }
  };
}

/**
 * Position-heatmap observer for the 3D viewport (or any stage container).
 * pointerdown → normalized (x, y) in 0..1 with 3 decimals →
 * `ui.interaction { x, y }`. The server buckets into its 48×27 grid.
 */
export function observeCanvasClicks(
  canvasOrContainer: HTMLElement | ShadowRoot,
  track: TrackFn,
): () => void {
  const handler = (e: Event) => {
    try {
      const pe = e as PointerEvent;
      // A ShadowRoot has no box of its own — measure its host element instead.
      const el = ("host" in canvasOrContainer && (canvasOrContainer as ShadowRoot).host
        ? (canvasOrContainer as ShadowRoot).host
        : canvasOrContainer) as HTMLElement;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      const x = (pe.clientX - rect.left) / rect.width;
      const y = (pe.clientY - rect.top) / rect.height;
      if (x < 0 || x > 1 || y < 0 || y > 1) return;
      track("ui.interaction", {
        x: Math.round(x * 1000) / 1000,
        y: Math.round(y * 1000) / 1000,
      });
    } catch {
      /* noop */
    }
  };
  try {
    canvasOrContainer.addEventListener("pointerdown", handler);
  } catch {
    return () => undefined;
  }
  return () => {
    try {
      canvasOrContainer.removeEventListener("pointerdown", handler);
    } catch {
      /* noop */
    }
  };
}

/**
 * Bridge listener for viewer-emitted analytics (design §4.3 heatmap type 3 and
 * decisions log #3 — the viewer build posts these over the existing
 * postMessage bridge):
 *
 *   { type: 'dconfig:cameraSample', yaw, pitch, focused } → camera.orbit_sample
 *   { type: 'dconfig:viewerEvent', name, data }           → track(name, data)
 *                                          (name whitelisted against the
 *                                           canonical event taxonomy)
 */
export function listenForViewerMessages(track: TrackFn): () => void {
  const handler = (e: MessageEvent) => {
    try {
      const msg = e.data as { type?: unknown } | null;
      if (!msg || typeof msg !== "object") return;
      if (msg.type === "dconfig:cameraSample") {
        const m = msg as { yaw?: unknown; pitch?: unknown; focused?: unknown };
        if (typeof m.yaw !== "number" || typeof m.pitch !== "number") return;
        track("camera.orbit_sample", {
          yaw: m.yaw,
          pitch: m.pitch,
          focused: m.focused ?? null,
        });
        return;
      }
      if (msg.type === "dconfig:viewerEvent") {
        const m = msg as { name?: unknown; data?: unknown };
        if (typeof m.name !== "string" || !CANONICAL_SET.has(m.name)) return;
        const data = m.data && typeof m.data === "object"
          ? (m.data as Record<string, unknown>)
          : {};
        track(m.name, data);
      }
    } catch {
      /* noop */
    }
  };
  try {
    window.addEventListener("message", handler);
  } catch {
    return () => undefined;
  }
  return () => {
    try {
      window.removeEventListener("message", handler);
    } catch {
      /* noop */
    }
  };
}
